/** Rush Fever collect + prize award feature */

import {
  RUSH_FEVER_PRIZE_MAP,
  RUSH_FEVER_FREE_GAME_MULTIPLIER,
} from '../config/FeatureConfig';
import { Logger } from '../utils/Logger';

const logger = new Logger('RushFeverFeature');

export interface RushFeverState {
  rushCount: number;
  prizeMultiplier: number;
  featureLevel: number;
  isActive: boolean;
}

/** Feature level thresholds based on rush count */
function getFeatureLevel(rushCount: number): number {
  if (rushCount >= 7) return 3;
  if (rushCount >= 5) return 2;
  return 1;
}

/** Manages the Rush Fever collection and prize award */
export class RushFeverFeature {
  private _state: RushFeverState = {
    rushCount: 0,
    prizeMultiplier: 0,
    featureLevel: 0,
    isActive: false,
  };

  get state(): Readonly<RushFeverState> {
    return this._state;
  }

  get isActive(): boolean {
    return this._state.isActive;
  }

  /** Check whether Rush Fever should trigger */
  canTrigger(rushCount: number): boolean {
    return rushCount >= 3;
  }

  /**
   * Get the feature level (1-3) for the pile_diamond animation.
   *   3-4 Rush = level 1
   *   5-6 Rush = level 2
   *   7+  Rush = level 3
   */
  getFeatureLevel(rushCount: number): number {
    return getFeatureLevel(rushCount);
  }

  /**
   * Calculate the Rush Fever prize.
   * @param rushCount  Number of Rush symbols on the grid (3-9)
   * @param totalBet   The total bet for the spin
   * @param isFreeGame Whether the spin occurred during free games (2x multiplier)
   * @returns Prize amount in credits
   */
  calculatePrize(rushCount: number, totalBet: number, isFreeGame: boolean): number {
    const multiplier = RUSH_FEVER_PRIZE_MAP[rushCount];
    if (multiplier === undefined) {
      logger.warn(`No prize entry for rushCount=${rushCount}, defaulting to 0`);
      return 0;
    }

    let prize = multiplier * totalBet;

    if (isFreeGame) {
      prize *= RUSH_FEVER_FREE_GAME_MULTIPLIER;
      logger.info(
        `Rush Fever (free game): ${rushCount} Rush * ${multiplier}x * $${totalBet} * ${RUSH_FEVER_FREE_GAME_MULTIPLIER}x = $${prize.toFixed(4)}`
      );
    } else {
      logger.info(
        `Rush Fever: ${rushCount} Rush * ${multiplier}x * $${totalBet} = $${prize.toFixed(4)}`
      );
    }

    return prize;
  }

  /**
   * Start the Rush Fever feature with the given rush symbol count.
   * Calculates and returns the prize to award.
   */
  async start(rushCount: number, totalBet: number, isFreeGame: boolean): Promise<number> {
    this._state.isActive = true;
    this._state.rushCount = rushCount;
    this._state.featureLevel = getFeatureLevel(rushCount);
    this._state.prizeMultiplier = RUSH_FEVER_PRIZE_MAP[rushCount] ?? 0;

    logger.info(
      `=== RUSH FEVER START === count=${rushCount}, level=${this._state.featureLevel}, ` +
      `freeGame=${isFreeGame}`
    );

    // Calculate the prize
    const prize = this.calculatePrize(rushCount, totalBet, isFreeGame);

    logger.info(`Rush Fever prize awarded: $${prize.toFixed(4)}`);

    return prize;
  }

  /** End the Rush Fever presentation and clean up */
  end(): void {
    logger.info('=== RUSH FEVER END ===');
    this._state.isActive = false;
  }

  /** Reset state fully */
  reset(): void {
    this._state = {
      rushCount: 0,
      prizeMultiplier: 0,
      featureLevel: 0,
      isActive: false,
    };
  }
}
